import React, { useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import { Avatar } from 'primereact/avatar';
import { Dialog } from 'primereact/dialog';
import { GlobalInputField } from '../../components/GlobalComponents/GlobalInputField';
import { GlobalDropdownField, DropdownOption } from '../../components/GlobalComponents/GlobalDropdownField';
import apiClient from '../../stateManagers/apiSetup/axiosInterceptor';
import { useNavigate } from 'react-router-dom';
import { useOperatorProfileStore } from '../../stateManagers/stores/operatorProfileStore';
import PaymentHistoryModal from './parts/PaymentHistoryModal';

interface Batch {
  batch_id: string;
  batch_name: string;
  batch_time: string;
  course_id: string;
  course_name?: string;
  course_title?: string;
  is_active: boolean;
}

interface FeesInfo {
  total_fee: number;
  total_paid: number;
  balance_due: number;
  payment_status: 'not_paid' | 'partial' | 'fully_paid';
  payment_count: number;
  last_payment_date: string | null;
}

interface Student {
  student_id: string;
  enrollment_id: string;
  name: string;
  dateOfBirth: string | null;
  gender: string | null;
  mobileNumber: string | null;
  whatsappNumber: string | null;
  email: string | null;
  aadharNumber: string | null;
  category: string | null;
  district: string | null;
  state: string | null;
  pinCode: string | null;
  villageTown: string | null;
  profilePicture: any;
  status: string;
  date_of_admission: string;
  is_registered: boolean;
  fees_info: FeesInfo;
}

interface BatchInfo {
  batch_id: string;
  batch_name: string;
  batch_time: string;
  course_id: string;
  course_name: string | null;
  course_title: string | null;
  start_month?: string | null;
  start_year?: number | null;
  end_month?: string | null;
  end_year?: number | null;
  is_active: boolean;
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

interface MonthOption { key: string; month: string; year: number; label: string; }

function buildMonthOptions(batchInfo: BatchInfo | null, dateOfAdmission: string | null): MonthOption[] {
  let startIdx = batchInfo?.start_month ? MONTH_NAMES.indexOf(batchInfo.start_month) : -1;
  let startYear = batchInfo?.start_year ?? null;

  if (startIdx < 0 || startYear === null) {
    if (dateOfAdmission) {
      const d = new Date(dateOfAdmission);
      if (!isNaN(d.getTime())) {
        startIdx = d.getMonth();
        startYear = d.getFullYear();
      }
    }
  }

  if (startIdx < 0 || startYear === null) return [];

  const now = new Date();
  let endIdx = now.getMonth();
  let endYear = now.getFullYear();

  const batchEndIdx = batchInfo?.end_month ? MONTH_NAMES.indexOf(batchInfo.end_month) : -1;
  const batchEndYear = batchInfo?.end_year ?? null;
  if (batchEndIdx >= 0 && batchEndYear !== null && batchEndYear * 12 + batchEndIdx > endYear * 12 + endIdx) {
    endIdx = batchEndIdx;
    endYear = batchEndYear;
  }

  const options: MonthOption[] = [];
  let cy = startYear;
  let cm = startIdx;
  const endTotal = endYear * 12 + endIdx;
  let safety = 0;

  while (cy * 12 + cm <= endTotal && safety < 60) {
    options.push({ key: `${cy}-${cm}`, month: MONTH_NAMES[cm], year: cy, label: `${MONTH_NAMES[cm].slice(0, 3)} ${cy}` });
    cm++;
    if (cm > 11) { cm = 0; cy++; }
    safety++;
  }

  return options;
}

interface FeeStructure {
  base_fee: number;
  additional_fee: number;
  total_fee: number;
}

interface Summary {
  total_students: number;
  fully_paid: number;
  partial_paid: number;
  not_paid: number;
  total_collected: number;
  total_pending: number;
  expected_total: number;
}

const StudentsFees: React.FC = () => {
  const [students, setStudents] = useState<Student[]>([]);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [batchesLoading, setBatchesLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [tempSearch, setTempSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 10, pages: 1 });
  const [statusFilter, setStatusFilter] = useState<string>('active');
  const [batches, setBatches] = useState<Batch[]>([]);
  const [selectedBatchId, setSelectedBatchId] = useState<string | null>(null);
  const [batchInfo, setBatchInfo] = useState<BatchInfo | null>(null);
  const [feeStructure, setFeeStructure] = useState<FeeStructure | null>(null);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [showPaymentHistoryModal, setShowPaymentHistoryModal] = useState(false);
  const [selectedStudentForHistory, setSelectedStudentForHistory] = useState<{
    studentId: string; enrollmentId: string; studentName: string; courseName: string;
  } | null>(null);

  const { profile } = useOperatorProfileStore();
  const INSTITUTE_ID = profile?.institute_id;
  const BRANCH_ID = profile?.branch_id;

  const statuses: DropdownOption[] = [
    { label: 'Active Students', value: 'active' },
    { label: 'Inactive Students', value: 'inactive' },
    { label: 'All Students', value: 'all' }
  ];

  const limitOptions: DropdownOption[] = [
    { label: 'Show 5', value: 5 },
    { label: 'Show 10', value: 10 },
    { label: 'Show 20', value: 20 },
    { label: 'Show 50', value: 50 }
  ];

  useEffect(() => { fetchBatches(); }, []);

  useEffect(() => {
    if (selectedBatchId) fetchStudentsByBatch();
  }, [selectedBatchId, page, limit, search, statusFilter]);

  const fetchBatches = async () => {
    try {
      setBatchesLoading(true);
      const response = await apiClient.get('/v1/batches');
      if (response.data.success) {
        const activeBatches = response.data.data.filter((b: Batch) => b.is_active);
        setBatches(activeBatches);
        if (activeBatches.length > 0 && !selectedBatchId) {
          setSelectedBatchId(activeBatches[0].batch_id);
        }
      }
    } catch (err: any) {
      setError('Failed to load batches');
    } finally {
      setBatchesLoading(false);
    }
  };

  const fetchStudentsByBatch = async () => {
    if (!selectedBatchId) return;
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams({
        page: page.toString(), limit: limit.toString(),
        status: statusFilter, ...(search && { search })
      });
      const response = await apiClient.get(`/v1/fees/students-by-batch/${selectedBatchId}?${params}`);
      if (response.data.success) {
        console.log(response.data)
        setStudents(response.data.data);
        setBatchInfo(response.data.batch_info);
        setFeeStructure(response.data.fee_structure);
        setSummary(response.data.summary);
        setPagination(response.data.pagination);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load students');
      setStudents([]);
    } finally {
      setLoading(false);
    }
  };

  const handleBatchClick = (batchId: string) => {
    setSelectedBatchId(batchId);
    setPage(1); setSearch(''); setTempSearch('');
  };

  const handleSearch = () => { setSearch(tempSearch); setPage(1); };
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => setTempSearch(e.target.value);
  const formatDate = (dateString: string | null) => dateString || '-';

  const getPaymentStatusStyle = (status: string) => {
    switch (status) {
      case 'fully_paid':
        return { background: 'rgba(22,163,74,0.12)', color: '#15803d', border: '1px solid rgba(22,163,74,0.3)' };
      case 'partial':
        return { background: 'rgba(251,178,31,0.12)', color: '#b8860b', border: '1px solid rgba(251,178,31,0.35)' };
      case 'not_paid':
        return { background: 'rgba(239,68,68,0.1)', color: '#c62828', border: '1px solid rgba(239,68,68,0.3)' };
      default:
        return { background: 'rgba(100,116,139,0.1)', color: '#475569', border: '1px solid rgba(100,116,139,0.2)' };
    }
  };

  const getPaymentStatusLabel = (status: string) => {
    switch (status) {
      case 'fully_paid': return 'Fully Paid';
      case 'partial': return 'Partial';
      case 'not_paid': return 'Not Paid';
      default: return status;
    }
  };

  const getPaymentStatusIcon = (status: string) => {
    switch (status) {
      case 'fully_paid': return 'pi-check-circle';
      case 'partial': return 'pi-clock';
      case 'not_paid': return 'pi-exclamation-triangle';
      default: return 'pi-info-circle';
    }
  };

  const colHeaderStyle: React.CSSProperties = {
    background: '#fafafa', color: '#9ca3af',
    fontSize: '0.72rem', fontWeight: '700', textTransform: 'uppercase',
    borderBottom: '2px solid #E5E7EB', padding: '14px 16px', letterSpacing: '0.6px'
  };
  const colBodyStyle: React.CSSProperties = {
    padding: '14px 16px', borderBottom: '1px solid #f3f4f6'
  };

  const profilePictureTemplate = (rowData: Student) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
      <div style={{ position: 'relative' }}>
        <Avatar
          image={rowData.profilePicture?.cdn_url || undefined}
          icon={!rowData.profilePicture ? 'pi pi-user' : undefined}
          size="normal" shape="circle"
          style={{
            width: '40px', height: '40px',
            backgroundColor: rowData.profilePicture ? 'transparent' : '#E8EEF7',
            color: '#0e225d', border: '2px solid #E5E7EB',
            boxShadow: '0 2px 8px rgba(14,34,93,0.1)'
          }}
        />
        {rowData.is_registered && (
          <div style={{
            position: 'absolute', bottom: '-2px', right: '-2px',
            width: '14px', height: '14px', background: '#16a34a',
            borderRadius: '50%', border: '2px solid white',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <i className="pi pi-check" style={{ fontSize: '0.5rem', color: 'white' }}></i>
          </div>
        )}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <span style={{ color: '#0e225d', fontSize: '0.85rem', fontWeight: '600' }}>{rowData.name}</span>
        <span style={{ fontSize: '0.7rem', color: '#9ca3af' }}>{rowData.mobileNumber || '-'}</span>
      </div>
    </div>
  );

  const feesInfoTemplate = (rowData: Student) => {
    const { fees_info } = rowData;
    const progress = fees_info.total_fee > 0 ? (fees_info.total_paid / fees_info.total_fee) * 100 : 0;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        <div style={{ fontSize: '0.85rem', fontWeight: '700', color: '#0e225d' }}>
          ₹{fees_info.total_paid.toLocaleString('en-IN')}
          <span style={{ color: '#9ca3af', fontWeight: '400', fontSize: '0.78rem' }}>
            {' '}/ ₹{fees_info.total_fee.toLocaleString('en-IN')}
          </span>
        </div>
        <div style={{ height: '4px', background: '#f3f4f6', borderRadius: '4px', overflow: 'hidden', width: '120px' }}>
          <div style={{
            height: '100%', width: `${Math.min(progress, 100)}%`,
            background: progress >= 100 ? '#16a34a' : progress > 0 ? '#fbb21f' : '#ef4444',
            borderRadius: '4px', transition: 'width 0.3s ease'
          }} />
        </div>
        <div style={{ fontSize: '0.72rem', fontWeight: '600', color: fees_info.balance_due > 0 ? '#c62828' : '#15803d' }}>
          Balance: ₹{fees_info.balance_due.toLocaleString('en-IN')}
        </div>
      </div>
    );
  };

  const paymentStatusTemplate = (rowData: Student) => {
    const style = getPaymentStatusStyle(rowData.fees_info.payment_status);
    const icon = getPaymentStatusIcon(rowData.fees_info.payment_status);
    return (
      <span style={{
        padding: '4px 12px', borderRadius: '20px',
        fontSize: '0.72rem', fontWeight: '700',
        display: 'inline-flex', alignItems: 'center', gap: '4px',
        letterSpacing: '0.3px', ...style
      }}>
        <i className={`pi ${icon}`} style={{ fontSize: '0.7rem' }}></i>
        {getPaymentStatusLabel(rowData.fees_info.payment_status)}
      </span>
    );
  };

  const actionBodyTemplate = (rowData: Student) => (
    <div style={{ display: 'flex', gap: '6px', justifyContent: 'center', flexWrap: 'wrap' }}>
      <button
        onClick={() => navigate(`/students/${rowData.student_id}`)}
        title="View Details"
        style={{
          padding: '6px 12px',
          height: '32px',
          background: 'transparent',
          border: '1px solid #E5E7EB',
          borderRadius: '8px',
          cursor: 'pointer',
          color: '#9ca3af',
          transition: 'all 0.2s ease',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '5px',
          fontSize: '0.75rem',
          fontWeight: '600',
          whiteSpace: 'nowrap'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(14,34,93,0.08)';
          e.currentTarget.style.borderColor = 'rgba(14,34,93,0.2)';
          e.currentTarget.style.color = '#0e225d';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'transparent';
          e.currentTarget.style.borderColor = '#E5E7EB';
          e.currentTarget.style.color = '#9ca3af';
        }}
      >
        <i className="pi pi-eye" style={{ fontSize: '0.8rem' }}></i>
        View
      </button>

      <button
        onClick={() => { setSelectedStudent(rowData); setShowPaymentModal(true); }}
        title="Record Payment"
        style={{
          padding: '6px 12px',
          height: '32px',
          background: 'transparent',
          border: '1px solid #E5E7EB',
          borderRadius: '8px',
          cursor: 'pointer',
          color: '#9ca3af',
          transition: 'all 0.2s ease',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '5px',
          fontSize: '0.75rem',
          fontWeight: '600',
          whiteSpace: 'nowrap'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(251,178,31,0.12)';
          e.currentTarget.style.borderColor = 'rgba(251,178,31,0.35)';
          e.currentTarget.style.color = '#b8860b';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'transparent';
          e.currentTarget.style.borderColor = '#E5E7EB';
          e.currentTarget.style.color = '#9ca3af';
        }}
      >
        <i className="pi pi-wallet" style={{ fontSize: '0.8rem' }}></i>
        Record
      </button>

      <button
        onClick={() => {
          setSelectedStudentForHistory({
            studentId: rowData.student_id, enrollmentId: rowData.enrollment_id,
            studentName: rowData.name, courseName: batchInfo?.course_name || ''
          });
          setShowPaymentHistoryModal(true);
        }}
        title="Payment History"
        style={{
          padding: '6px 12px',
          height: '32px',
          background: 'transparent',
          border: '1px solid #E5E7EB',
          borderRadius: '8px',
          cursor: 'pointer',
          color: '#9ca3af',
          transition: 'all 0.2s ease',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '5px',
          fontSize: '0.75rem',
          fontWeight: '600',
          whiteSpace: 'nowrap'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(22,163,74,0.1)';
          e.currentTarget.style.borderColor = 'rgba(22,163,74,0.3)';
          e.currentTarget.style.color = '#15803d';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'transparent';
          e.currentTarget.style.borderColor = '#E5E7EB';
          e.currentTarget.style.color = '#9ca3af';
        }}
      >
        <i className="pi pi-history" style={{ fontSize: '0.8rem' }}></i>
        Payments
      </button>
    </div>
  );

  const renderPaginationNumbers = () => {
    const pages: (number | string)[] = [];
    const totalPages = pagination.pages;
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (page <= 3) pages.push(1, 2, 3, '...', totalPages);
      else if (page >= totalPages - 2) pages.push(1, '...', totalPages - 2, totalPages - 1, totalPages);
      else pages.push(1, '...', page, '...', totalPages);
    }
    return pages.map((p, idx) =>
      p === '...' ? (
        <span key={`e-${idx}`} style={{ padding: '0 6px', color: '#9ca3af', fontSize: '0.85rem' }}>…</span>
      ) : (
        <button key={p} onClick={() => setPage(p as number)} style={{
          minWidth: '36px', height: '36px', padding: '0 10px',
          background: page === p ? 'linear-gradient(135deg, #0e225d 0%, #1a3370 100%)' : 'transparent',
          color: page === p ? '#fbb21f' : '#0e225d',
          border: page === p ? 'none' : '1px solid #E5E7EB',
          borderRadius: '8px', fontSize: '0.85rem', fontWeight: page === p ? '700' : '500',
          cursor: 'pointer', boxShadow: page === p ? '0 2px 8px rgba(14,34,93,0.25)' : 'none',
          transition: 'all 0.2s ease'
        }}
          onMouseEnter={(e) => { if (page !== p) { e.currentTarget.style.background = 'rgba(14,34,93,0.06)'; e.currentTarget.style.borderColor = 'rgba(14,34,93,0.2)'; } }}
          onMouseLeave={(e) => { if (page !== p) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = '#E5E7EB'; } }}
        >{p}</button>
      )
    );
  };

  if (batchesLoading || (loading && students.length === 0 && !selectedBatchId)) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px', background: 'white', borderRadius: '16px', border: '1px solid #E5E7EB' }}>
        <div style={{ textAlign: 'center' }}>
          <ProgressSpinner style={{ width: '48px', height: '48px' }} strokeWidth="3" animationDuration=".8s" />
          <p style={{ marginTop: '16px', color: '#0e225d', fontSize: '0.9rem', fontWeight: '500' }}>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '0', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <div style={{ background: 'white', borderRadius: '14px', padding: '14px 18px', boxShadow: '0 1px 4px rgba(14,34,93,0.06)', border: '1px solid #E5E7EB' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
          <div style={{ width: '4px', height: '18px', background: 'linear-gradient(180deg, #fbb21f 0%, #e09a00 100%)', borderRadius: '4px' }} />
          <span style={{ fontSize: '0.82rem', fontWeight: '700', color: '#0e225d', letterSpacing: '0.3px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <i className="pi pi-th-large" style={{ fontSize: '0.8rem' }}></i>
            Select Batch
          </span>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
          {batches.map((batch) => {
            const isSelected = selectedBatchId === batch.batch_id;
            return (
              <div
                key={batch.batch_id}
                onClick={() => handleBatchClick(batch.batch_id)}
                style={{
                  padding: '10px 18px',
                  background: isSelected ? 'linear-gradient(135deg, #0e225d 0%, #1a3370 100%)' : 'white',
                  border: isSelected ? '2px solid transparent' : '2px solid #E5E7EB',
                  borderRadius: '24px',
                  cursor: 'pointer',
                  transition: 'all 0.25s cubic-bezier(0.4,0,0.2,1)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  boxShadow: isSelected ? '0 4px 14px rgba(14,34,93,0.25)' : 'none'
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.background = 'rgba(14,34,93,0.04)';
                    e.currentTarget.style.borderColor = 'rgba(14,34,93,0.25)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(14,34,93,0.1)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.background = 'white';
                    e.currentTarget.style.borderColor = '#E5E7EB';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }
                }}
              >
                <div style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: '50%',
                  background: isSelected ? '#fbb21f' : 'rgba(14,34,93,0.08)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <i className="pi pi-users" style={{ fontSize: '0.7rem', color: '#0e225d' }}></i>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <span style={{
                    fontSize: '0.70rem',
                    fontWeight: '600',
                    color: isSelected ? 'white' : '#0e225d',
                    lineHeight: '1.2'
                  }}>
                    {batch.batch_name}
                  </span>
                  <span style={{
                    fontSize: '0.6rem',
                    color: isSelected ? 'rgba(255,255,255,0.65)' : '#9ca3af',
                    lineHeight: '1.2'
                  }}>
                    {batch.batch_time}
                  </span>
                </div>
                {isSelected && <i className="pi pi-check" style={{ fontSize: '0.75rem', color: '#fbb21f', marginLeft: '2px' }}></i>}
              </div>
            );
          })}
          {batches.length === 0 && (
            <div style={{
              padding: '40px 20px',
              textAlign: 'center',
              background: 'rgba(14,34,93,0.03)',
              borderRadius: '12px',
              border: '2px dashed #E5E7EB',
              width: '100%'
            }}>
              <p style={{ margin: 0, color: '#9ca3af', fontSize: '0.9rem' }}>No active batches found</p>
            </div>
          )}
        </div>
      </div>

      {selectedBatchId && summary && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: '12px',
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #0e225d 0%, #1a3370 100%)',
            padding: '16px',
            borderRadius: '14px',
            color: 'white',
            boxShadow: '0 4px 14px rgba(14,34,93,0.25)',
            position: 'relative',
            overflow: 'hidden'
          }}>
            <div style={{
              position: 'absolute',
              top: '-20px',
              right: '-20px',
              width: '70px',
              height: '70px',
              background: 'rgba(251,178,31,0.1)',
              borderRadius: '50%',
              filter: 'blur(15px)'
            }} />
            <div style={{
              fontSize: '0.68rem',
              color: 'rgba(255,255,255,0.6)',
              marginBottom: '4px',
              textTransform: 'uppercase',
              letterSpacing: '0.6px',
              fontWeight: '600'
            }}>
              Total
            </div>
            <div style={{ fontSize: '0.9rem', fontWeight: '700' }}>{summary.total_students}</div>
          </div>
          <div style={{
            background: 'white',
            padding: '16px',
            borderRadius: '14px',
            border: '1px solid rgba(22,163,74,0.2)',
            boxShadow: '0 2px 8px rgba(22,163,74,0.08)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
              <i className="pi pi-check-circle" style={{ fontSize: '0.75rem', color: '#15803d' }}></i>
              <div style={{
                fontSize: '0.68rem',
                color: '#9ca3af',
                textTransform: 'uppercase',
                letterSpacing: '0.6px',
                fontWeight: '600'
              }}>
                Fully Paid
              </div>
            </div>
            <div style={{ fontSize: '0.9rem', fontWeight: '700', color: '#15803d' }}>{summary.fully_paid}</div>
          </div>
          <div style={{
            background: 'white',
            padding: '16px',
            borderRadius: '14px',
            border: '1px solid rgba(251,178,31,0.25)',
            boxShadow: '0 2px 8px rgba(251,178,31,0.08)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
              <i className="pi pi-clock" style={{ fontSize: '0.75rem', color: '#b8860b' }}></i>
              <div style={{
                fontSize: '0.68rem',
                color: '#9ca3af',
                textTransform: 'uppercase',
                letterSpacing: '0.6px',
                fontWeight: '600'
              }}>
                Partial
              </div>
            </div>
            <div style={{ fontSize: '0.9rem', fontWeight: '700', color: '#b8860b' }}>{summary.partial_paid}</div>
          </div>
          <div style={{
            background: 'white',
            padding: '16px',
            borderRadius: '14px',
            border: '1px solid rgba(239,68,68,0.2)',
            boxShadow: '0 2px 8px rgba(239,68,68,0.08)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
              <i className="pi pi-exclamation-triangle" style={{ fontSize: '0.75rem', color: '#c62828' }}></i>
              <div style={{
                fontSize: '0.68rem',
                color: '#9ca3af',
                textTransform: 'uppercase',
                letterSpacing: '0.6px',
                fontWeight: '600'
              }}>
                Not Paid
              </div>
            </div>
            <div style={{ fontSize: '0.9rem', fontWeight: '700', color: '#c62828' }}>{summary.not_paid}</div>
          </div>
          <div style={{
            background: 'white',
            padding: '16px',
            borderRadius: '14px',
            border: '1px solid rgba(14,34,93,0.15)',
            boxShadow: '0 2px 8px rgba(14,34,93,0.06)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
              <i className="pi pi-arrow-down" style={{ fontSize: '0.75rem', color: '#0e225d' }}></i>
              <div style={{
                fontSize: '0.68rem',
                color: '#9ca3af',
                textTransform: 'uppercase',
                letterSpacing: '0.6px',
                fontWeight: '600'
              }}>
                Collected
              </div>
            </div>
            <div style={{ fontSize: '0.9rem', fontWeight: '700', color: '#0e225d' }}>₹{(summary.total_collected / 1000).toFixed(1)}K</div>
          </div>
          <div style={{
            background: 'white',
            padding: '16px',
            borderRadius: '14px',
            border: '1px solid rgba(239,68,68,0.15)',
            boxShadow: '0 2px 8px rgba(239,68,68,0.06)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
              <i className="pi pi-arrow-up" style={{ fontSize: '0.75rem', color: '#c62828' }}></i>
              <div style={{
                fontSize: '0.68rem',
                color: '#9ca3af',
                textTransform: 'uppercase',
                letterSpacing: '0.6px',
                fontWeight: '600'
              }}>
                Pending
              </div>
            </div>
            <div style={{ fontSize: '0.9rem', fontWeight: '700', color: '#c62828' }}>₹{(summary.total_pending / 1000).toFixed(1)}K</div>
          </div>
        </div>
      )}

      {selectedBatchId && (
        <div style={{
          background: 'white',
          padding: '14px 18px',
          borderRadius: '14px',
          boxShadow: '0 1px 4px rgba(14,34,93,0.06)',
          border: '1px solid #E5E7EB'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
            <div style={{ width: '4px', height: '18px', background: 'linear-gradient(180deg, #fbb21f 0%, #e09a00 100%)', borderRadius: '4px' }} />
            <span style={{ fontSize: '0.82rem', fontWeight: '700', color: '#0e225d', letterSpacing: '0.3px' }}>Filter & Search</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr auto', gap: '16px', alignItems: 'end' }}>
            <GlobalInputField
              label="Search Students"
              value={tempSearch}
              onChange={handleInputChange}
              placeholder="Search by name, mobile, email..."
              icon="pi pi-search"
            />
            <GlobalDropdownField
              label="Status"
              value={statusFilter}
              onChange={(val) => { setStatusFilter(val); setPage(1); }}
              options={statuses}
              placeholder="Active"
              icon="pi pi-info-circle"
            />
            <button
              onClick={handleSearch}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '0 20px',
                height: '44px',
                background: 'linear-gradient(135deg, #0e225d 0%, #1a3370 100%)',
                border: 'none',
                borderRadius: '10px',
                color: 'white',
                fontSize: '0.82rem',
                fontWeight: '700',
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(14,34,93,0.25)',
                transition: 'all 0.2s ease',
                letterSpacing: '0.3px',
                whiteSpace: 'nowrap'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 4px 14px rgba(14,34,93,0.35)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(14,34,93,0.25)';
              }}
            >
              <i className="pi pi-search" style={{ fontSize: '0.82rem' }}></i>
              SEARCH
            </button>
          </div>
        </div>
      )}

      {selectedBatchId && (
        <div style={{
          background: 'white',
          borderRadius: '14px',
          boxShadow: '0 1px 4px rgba(14,34,93,0.06)',
          border: '1px solid #E5E7EB',
          overflow: 'hidden'
        }}>
          <div style={{
            padding: '14px 18px',
            borderBottom: '1px solid #E5E7EB',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '12px',
            flexWrap: 'wrap'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ width: '4px', height: '22px', background: 'linear-gradient(180deg, #fbb21f 0%, #e09a00 100%)', borderRadius: '4px' }} />
              <h2 style={{ margin: 0, fontSize: '0.9rem', fontWeight: '700', color: '#0e225d' }}>Students List</h2>
            </div>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <span style={{ fontSize: '0.82rem', color: '#9ca3af', fontWeight: '600' }}>Show</span>
              <GlobalDropdownField
                value={limit}
                onChange={(val) => { setLimit(val); setPage(1); }}
                options={limitOptions}
                placeholder="Show 10"
              />
            </div>
          </div>

          <DataTable
            value={students}
            dataKey="student_id"
            loading={loading}
            emptyMessage="No students found"
            className="fees-dt"
            style={{ fontSize: '0.85rem' }}
          >
            <Column field="name" header="Student" body={profilePictureTemplate} headerStyle={{ ...colHeaderStyle, minWidth: '220px' }} bodyStyle={colBodyStyle} />
            <Column field="date_of_admission" header="Admission" body={(rowData) => <span style={{ color: '#6b7280', fontSize: '0.83rem' }}>{formatDate(rowData.date_of_admission)}</span>} headerStyle={colHeaderStyle} bodyStyle={colBodyStyle} />
            <Column field="fees_info" header="Fees (Paid / Total)" body={feesInfoTemplate} headerStyle={{ ...colHeaderStyle, minWidth: '180px' }} bodyStyle={colBodyStyle} />
            <Column field="fees_info.payment_status" header="Status" body={paymentStatusTemplate} headerStyle={colHeaderStyle} bodyStyle={colBodyStyle} />
            <Column
              field="fees_info.payment_count"
              header="Payments"
              body={(rowData) => (
                <div style={{ textAlign: 'center', fontWeight: '700', color: rowData.fees_info.payment_count > 0 ? '#0e225d' : '#9ca3af', fontSize: '0.9rem' }}>
                  {rowData.fees_info.payment_count || 0}
                </div>
              )}
              headerStyle={{ ...colHeaderStyle, textAlign: 'center' }}
              bodyStyle={{ ...colBodyStyle, textAlign: 'center' }}
            />
            <Column header="Actions" body={actionBodyTemplate} headerStyle={{ ...colHeaderStyle, minWidth: '260px', textAlign: 'center' }} bodyStyle={{ ...colBodyStyle, textAlign: 'center' }} />
          </DataTable>

          <div style={{
            padding: '14px 18px',
            borderTop: '1px solid #E5E7EB',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '10px',
            flexWrap: 'wrap'
          }}>
            <span style={{ fontSize: '0.8rem', color: '#9ca3af' }}>
              Showing {((page - 1) * limit) + 1}–{Math.min(page * limit, pagination.total)} of {pagination.total}
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <button
                onClick={() => setPage(1)}
                disabled={page === 1}
                style={{
                  width: '30px',
                  height: '30px',
                  background: 'transparent',
                  border: '1px solid #E5E7EB',
                  borderRadius: '7px',
                  cursor: page === 1 ? 'not-allowed' : 'pointer',
                  color: '#0e225d',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <i className="pi pi-angle-double-left" style={{ fontSize: '0.7rem' }}></i>
              </button>
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                style={{
                  width: '30px',
                  height: '30px',
                  background: 'transparent',
                  border: '1px solid #E5E7EB',
                  borderRadius: '7px',
                  cursor: page === 1 ? 'not-allowed' : 'pointer',
                  color: '#0e225d',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <i className="pi pi-angle-left" style={{ fontSize: '0.7rem' }}></i>
              </button>

              {renderPaginationNumbers()}

              <button
                onClick={() => setPage(p => Math.min(pagination.pages, p + 1))}
                disabled={page === pagination.pages}
                style={{
                  width: '30px',
                  height: '30px',
                  background: 'transparent',
                  border: '1px solid #E5E7EB',
                  borderRadius: '7px',
                  cursor: page === pagination.pages ? 'not-allowed' : 'pointer',
                  color: '#0e225d',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <i className="pi pi-angle-right" style={{ fontSize: '0.7rem' }}></i>
              </button>
              <button
                onClick={() => setPage(pagination.pages)}
                disabled={page === pagination.pages}
                style={{
                  width: '30px',
                  height: '30px',
                  background: 'transparent',
                  border: '1px solid #E5E7EB',
                  borderRadius: '7px',
                  cursor: page === pagination.pages ? 'not-allowed' : 'pointer',
                  color: '#0e225d',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <i className="pi pi-angle-double-right" style={{ fontSize: '0.7rem' }}></i>
              </button>
            </div>
          </div>
        </div>
      )}

      {!selectedBatchId && batches.length > 0 && (
        <div style={{
          background: 'white',
          borderRadius: '14px',
          padding: '60px 20px',
          textAlign: 'center',
          boxShadow: '0 1px 4px rgba(14,34,93,0.06)',
          border: '1px solid #E5E7EB'
        }}>
          <h3 style={{ margin: '0 0 8px 0', color: '#0e225d', fontSize: '0.9rem', fontWeight: '700' }}>Select a Batch</h3>
          <p style={{ margin: 0, color: '#9ca3af', fontSize: '0.9rem' }}>Please select a batch from above to view students and their fees information</p>
        </div>
      )}

      {selectedStudent && (
        <PaymentModal
          visible={showPaymentModal}
          student={selectedStudent}
          feeStructure={feeStructure}
          batchInfo={batchInfo}
          onHide={() => { setShowPaymentModal(false); setSelectedStudent(null); }}
          onSuccess={() => { fetchStudentsByBatch(); }}
        />
      )}

      {selectedStudentForHistory && (
        <PaymentHistoryModal
          visible={showPaymentHistoryModal}
          studentId={selectedStudentForHistory.studentId}
          enrollmentId={selectedStudentForHistory.enrollmentId}
          studentName={selectedStudentForHistory.studentName}
          courseName={selectedStudentForHistory.courseName}
          onHide={() => { setShowPaymentHistoryModal(false); setSelectedStudentForHistory(null); }}
        />
      )}
    </div>
  );
};

const PaymentModal: React.FC<{
  visible: boolean;
  student: Student;
  feeStructure: FeeStructure | null;
  batchInfo: BatchInfo | null;
  onHide: () => void;
  onSuccess?: () => void;
}> = ({ visible, student, feeStructure, batchInfo, onHide, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    amount_paid: '',
    payment_method: 'cash',
    payment_date: new Date().toISOString().split('T')[0],
    transaction_id: '',
    upi_id: '',
    reference_number: '',
    cheque_number: '',
    bank_name: '',
    cheque_date: '',
    remarks: '',
    discount_amount: '',
    discount_reason: ''
  });
  const [selectedMonths, setSelectedMonths] = useState<string[]>([]);

  const paymentMethodOptions: DropdownOption[] = [
    { label: 'Cash', value: 'cash' }, { label: 'UPI', value: 'upi' },
    { label: 'Card', value: 'card' }, { label: 'Bank Transfer', value: 'bank_transfer' },
    { label: 'Cheque', value: 'cheque' }
  ];

  const monthOptions = React.useMemo(
    () => buildMonthOptions(batchInfo, student.date_of_admission),
    [batchInfo, student.date_of_admission]
  );

  const toggleMonth = (key: string) => {
    setSelectedMonths(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const discountValue = parseFloat(formData.discount_amount || '0') || 0;
  const amountValue   = parseFloat(formData.amount_paid   || '0') || 0;

  const handleSubmit = async () => {
    if (!formData.amount_paid || amountValue <= 0) {
      setError('Please enter a valid amount'); return;
    }
    if (discountValue < 0) {
      setError('Discount amount cannot be negative'); return;
    }
    setLoading(true); setError(null);
    try {
      const payload: any = {
        student_id: student.student_id,
        enrollment_id: student.enrollment_id,
        amount_paid: amountValue,
        payment_method: formData.payment_method,
        payment_date: formData.payment_date,
        remarks: formData.remarks || null,
        discount_amount: discountValue > 0 ? discountValue : 0,
        discount_reason: discountValue > 0 && formData.discount_reason ? formData.discount_reason : null
      };

      if (selectedMonths.length > 0) {
        payload.payment_for_months = selectedMonths
          .map(key => monthOptions.find(m => m.key === key))
          .filter((m): m is NonNullable<typeof m> => !!m)
          .map(m => ({ month: m.month, year: m.year }));
      }

      if (formData.payment_method === 'upi') {
        payload.transaction_id = formData.transaction_id || null;
        payload.upi_id = formData.upi_id || null;
      } else if (formData.payment_method === 'bank_transfer') {
        payload.reference_number = formData.reference_number || null;
        payload.bank_name = formData.bank_name || null;
      } else if (formData.payment_method === 'cheque') {
        payload.cheque_number = formData.cheque_number || null;
        payload.bank_name = formData.bank_name || null;
        payload.cheque_date = formData.cheque_date || null;
      } else if (formData.payment_method === 'card') {
        payload.transaction_id = formData.transaction_id || null;
      }

      const response = await apiClient.post('/v1/fees/payment', payload);
      if (response.data.success) { if (onSuccess) onSuccess(); onHide(); }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to record payment');
    } finally { setLoading(false); }
  };

  return (
    <Dialog
      visible={visible} onHide={onHide}
      style={{ width: '90vw', maxWidth: '580px' }}
      dismissableMask
      header={false}
      pt={{ root: { style: { borderRadius: '20px', overflow: 'hidden', border: '1px solid #E5E7EB', boxShadow: '0 20px 60px rgba(14,34,93,0.2)' } }, header: { style: { display: 'none' } }, content: { style: { padding: 0 } } }}
    >
      <div>
        <div style={{ background: 'linear-gradient(180deg, #0e225d 0%, #1a3370 100%)', padding: '24px', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: '-30px', right: '-30px', width: '120px', height: '120px', background: 'rgba(251,178,31,0.08)', borderRadius: '50%', filter: 'blur(25px)', pointerEvents: 'none' }} />
          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '3px 10px', background: 'rgba(251,178,31,0.18)', border: '1px solid rgba(251,178,31,0.35)', borderRadius: '20px', marginBottom: '10px', fontSize: '0.65rem', fontWeight: '700', letterSpacing: '0.8px', textTransform: 'uppercase', color: '#fbb21f' }}>
                  <i className="pi pi-wallet" style={{ fontSize: '0.65rem' }}></i>
                  Record Payment
                </div>
                <h3 style={{ margin: '0 0 4px 0', fontSize: '1.15rem', fontWeight: '700', color: 'white' }}>{student.name}</h3>
              </div>
              <button onClick={onHide} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: '8px', width: '32px', height: '32px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.7)', transition: 'all 0.2s ease' }} onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.2)'; e.currentTarget.style.color = 'white'; }} onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; e.currentTarget.style.color = 'rgba(255,255,255,0.7)'; }}>
                <i className="pi pi-times" style={{ fontSize: '0.8rem' }}></i>
              </button>
            </div>

            {feeStructure && (
              <div style={{ marginTop: '12px', padding: '12px 16px', background: 'rgba(255,255,255,0.08)', borderRadius: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '1px solid rgba(255,255,255,0.1)', flexWrap: 'wrap', gap: '8px' }}>
                <div>
                  <div style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.55)', marginBottom: '2px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Balance Due</div>
                  <div style={{ fontSize: '0.9rem', fontWeight: '700', color: '#fbb21f' }}>
                    ₹{student.fees_info.balance_due.toLocaleString('en-IN')}
                  </div>
                </div>
                {discountValue > 0 && (
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.55)', marginBottom: '2px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Discount</div>
                    <div style={{ fontSize: '0.9rem', fontWeight: '700', color: '#86efac' }}>
                      − ₹{discountValue.toLocaleString('en-IN')}
                    </div>
                  </div>
                )}
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.55)', marginBottom: '2px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total Fee</div>
                  <div style={{ fontSize: '0.9rem', fontWeight: '600', color: 'rgba(255,255,255,0.85)' }}>
                    ₹{feeStructure.total_fee.toLocaleString('en-IN')}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div style={{ padding: '24px' }}>
          {error && <Message severity="error" text={error} style={{ marginBottom: '16px', width: '100%' }} />}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <GlobalInputField label="Amount" required type="number" value={formData.amount_paid} onChange={(e) => setFormData({ ...formData, amount_paid: e.target.value })} placeholder="Enter amount" icon="pi pi-money-bill" />
            <GlobalDropdownField label="Payment Method" required value={formData.payment_method} onChange={(val) => setFormData({ ...formData, payment_method: val })} options={paymentMethodOptions} />

            {formData.payment_method === 'upi' && (<>
              <GlobalInputField label="Transaction ID" value={formData.transaction_id} onChange={(e) => setFormData({ ...formData, transaction_id: e.target.value })} placeholder="Enter UPI transaction ID" />
              <GlobalInputField label="UPI ID" value={formData.upi_id} onChange={(e) => setFormData({ ...formData, upi_id: e.target.value })} placeholder="example@paytm" />
            </>)}
            {formData.payment_method === 'bank_transfer' && (<>
              <GlobalInputField label="Reference Number" required value={formData.reference_number} onChange={(e) => setFormData({ ...formData, reference_number: e.target.value })} placeholder="Enter reference number" />
              <GlobalInputField label="Bank Name" value={formData.bank_name} onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })} placeholder="Enter bank name" />
            </>)}
            {formData.payment_method === 'cheque' && (<>
              <GlobalInputField label="Cheque Number" required value={formData.cheque_number} onChange={(e) => setFormData({ ...formData, cheque_number: e.target.value })} placeholder="Enter cheque number" />
              <GlobalInputField label="Bank Name" required value={formData.bank_name} onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })} placeholder="Enter bank name" />
              <GlobalInputField label="Cheque Date" type="date" value={formData.cheque_date} onChange={(e) => setFormData({ ...formData, cheque_date: e.target.value })} />
            </>)}
            {formData.payment_method === 'card' && (
              <GlobalInputField label="Transaction ID" value={formData.transaction_id} onChange={(e) => setFormData({ ...formData, transaction_id: e.target.value })} placeholder="Enter card transaction ID" />
            )}

            <GlobalInputField label="Payment Date" type="date" value={formData.payment_date} onChange={(e) => setFormData({ ...formData, payment_date: e.target.value })} />
            <GlobalInputField label="Remarks" value={formData.remarks} onChange={(e) => setFormData({ ...formData, remarks: e.target.value })} placeholder="Optional remarks" />

            {monthOptions.length > 0 && (
              <div style={{ padding: '16px', background: 'rgba(14,34,93,0.04)', border: '1px solid rgba(14,34,93,0.12)', borderRadius: '12px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <i className="pi pi-calendar" style={{ fontSize: '0.78rem', color: '#0e225d' }}></i>
                  <span style={{ fontSize: '0.73rem', fontWeight: '700', color: '#0e225d', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Paid For (optional — select month(s))
                  </span>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {monthOptions.map(m => {
                    const isSelected = selectedMonths.includes(m.key);
                    return (
                      <div
                        key={m.key}
                        onClick={() => toggleMonth(m.key)}
                        style={{
                          padding: '6px 12px', borderRadius: '16px', cursor: 'pointer',
                          fontSize: '0.75rem', fontWeight: '600',
                          background: isSelected ? '#0e225d' : '#f5f5f5',
                          color: isSelected ? '#fbb21f' : '#0e225d',
                          border: isSelected ? '1px solid #0e225d' : '1px solid transparent',
                          transition: 'all 0.15s ease'
                        }}
                      >
                        {m.label}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div style={{ padding: '16px', background: 'rgba(251,178,31,0.06)', border: '1px solid rgba(251,178,31,0.25)', borderRadius: '12px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <i className="pi pi-tag" style={{ fontSize: '0.78rem', color: '#b8860b' }}></i>
                <span style={{ fontSize: '0.73rem', fontWeight: '700', color: '#b8860b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Discount (optional)
                </span>
              </div>
              <GlobalInputField
                label="Discount Amount"
                type="number"
                value={formData.discount_amount}
                onChange={(e) => setFormData({ ...formData, discount_amount: e.target.value })}
                placeholder="0"
                icon="pi pi-money-bill"
              />
              {discountValue > 0 && (
                <GlobalInputField
                  label="Discount Reason"
                  value={formData.discount_reason}
                  onChange={(e) => setFormData({ ...formData, discount_reason: e.target.value })}
                  placeholder="e.g. Merit scholarship, early payment..."
                />
              )}
            </div>
          </div>

          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px', paddingTop: '16px', borderTop: '1px solid #f3f4f6' }}>
            <button
              onClick={onHide} disabled={loading}
              style={{
                padding: '10px 20px',
                background: 'white',
                border: '1px solid #E5E7EB',
                borderRadius: '10px',
                color: '#6b7280',
                fontSize: '0.85rem',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => { if (!loading) { e.currentTarget.style.borderColor = '#0e225d'; e.currentTarget.style.color = '#0e225d'; } }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#E5E7EB'; e.currentTarget.style.color = '#6b7280'; }}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit} disabled={loading}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '10px 22px',
                background: loading ? 'rgba(14,34,93,0.5)' : 'linear-gradient(135deg, #0e225d 0%, #1a3370 100%)',
                border: 'none',
                borderRadius: '10px',
                color: 'white',
                fontSize: '0.85rem',
                fontWeight: '700',
                cursor: loading ? 'not-allowed' : 'pointer',
                boxShadow: '0 2px 8px rgba(14,34,93,0.25)',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => { if (!loading) { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 4px 14px rgba(14,34,93,0.35)'; } }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(14,34,93,0.25)'; }}
            >
              {loading && <i className="pi pi-spin pi-spinner" style={{ fontSize: '0.85rem' }}></i>}
              {loading ? 'Recording...' : 'Record Payment'}
            </button>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default StudentsFees;